import React, { useState, useCallback } from "react";
import { Form, SubmitButton, Input, Checkbox } from "formik-antd";
import { Formik, FormikHelpers, FormikProps } from "formik";
import { Row, Col, Tabs } from "antd";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { webServicesSchema, webServicesSchemaNoPwd } from "/app/src/schemas";
import { connectionService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { testConnection } from "/app/src/helpers/connectionsStatus";
import { Connection, Connection as ConnectionType } from "/app/src/models";
import { buildParams } from "/app/src/helpers/params";
import { handlePromiseError } from "/app/src/helpers/api";
import { handleSubmissionErrors, isCloudInstall } from "/app/src/helpers";
import useSetting from "/app/src/components/settings/setting";
import HeroButton from "/app/src/components/HeroUi/Button";
import Logs from "/app/src/components/settings/connection/logs";
import Box from "/app/src/components/generic/components/box";
import {
  IconBuilder,
  IconToolTip,
} from "/app/src/components/icons/IconBuilder";
import { useConnections } from "/app/src/contexts/ConnectionsStatusContext";
import { useStatusContext } from "/app/src/contexts/hooks/useStatusContext";

/**
 * Formats the form values to be sent to the API
 * @param values Form values
 */
function formatForm(values: FormValues) {
  const formatted: {
    username: string | undefined;
    url: string | undefined;
    password?: string;
  } = {
    username: values.username,
    url: values.url,
  };
  if (values.password) {
    formatted.password = values.password;
  }
  return formatted;
}

interface FormValues {
  url: string | undefined;
  username: string | undefined;
  password?: string;
  setting?: boolean;
}

/**
 * Component to display the PowerPick Web Services connection settings
 */
export default function WebServices() {
  const { t } = useTranslation();
  const { powerPick } = useConnections();
  const [isTesting, setIsTesting] = useState(false);
  const { settingValue, createUpdateSetting } = useSetting({ number: 201 });
  const { installType } = useStatusContext();

  const queryClient = useQueryClient();

  const { data: connection, isFetching } = useQuery({
    queryKey: ["connections1", { type: "PowerPick Web Services" }],
    queryFn: () => {
      return connectionService
        .getAll(buildParams({ type: "PowerPick Web Services" }))
        .then((response) => {
          return { connection: response.connections[0] };
        });
    },
    initialData: { connection: {} as ConnectionType },
    select: (data) => data.connection,
  });

  const { mutateAsync: updateConnection } = useMutation({
    mutationFn: (connection: ConnectionType) => {
      return connectionService
        .updateSingle(connection.id, connection)
        .then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["connections1", { type: "PowerPick Web Services" }],
        response,
      );
      queryClient.setQueryData(["powerpickConnection"], (old: Connection) => {
        return {
          ...old,
          status: response.status ? 1 : 0,
        };
      });
    },
  });

  //use callback for test handler
  const testHandler = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      if (!powerPick.status) {
        toast.error(t("translation:power_pick_connection_required"));
        return;
      }
      testConnection(setIsTesting, connection, updateConnection).finally(() => {
        queryClient.invalidateQueries(["powerpickConnection"]);
      });
    },
    [powerPick, connection, updateConnection, t, queryClient],
  );

  const onFocusHandler = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.placeholder = "";
    },
    [],
  );

  const onBlurHandler = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (connection.passwordExists) {
        e.target.placeholder = "***********";
      }
    },
    [connection.passwordExists],
  );

  /**
   * Component to display the PowerPick Web Services connection settings
   * @param param FormikProps<FormValues>
   * @returns JSX.Element
   */
  const connectionForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isValid }) => (
        <Form layout="vertical">
          <Row justify="start" gutter={16} style={{ paddingBottom: "10px" }}>
            <Col span={20}>
              <h2>{t("translation:web_services_connection")}</h2>
            </Col>
            <Col
              span={1}
              style={{
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {connection.status === 0 ? (
                <IconBuilder icon="Warning" color="#e00000" size={32} />
              ) : (
                <IconBuilder icon="CircleCheck" color="#23ce6b" size={32} />
              )}
            </Col>
            <Col span={3}>
              <div style={{ float: "right" }}>
                <HeroButton
                  size="sm"
                  variant="bordered"
                  color="default"
                  onClick={testHandler}
                  isDisabled={isTesting}
                  className="hover:border-primary-default hover:text-primary-default"
                >
                  {isTesting
                    ? t("translation:testing___")
                    : t("translation:test_") + t("translation:connection")}
                </HeroButton>
              </div>
            </Col>
          </Row>
          <Row justify="start" gutter={16}>
            <Col span={8}>
              <h3>{t("translation:web_services_url")}</h3>
              <div>
                <span>{t("translation:web_services_url_description")}</span>
                <IconToolTip
                  content={t("translation:web_services_url_tooltip")}
                  className="ml-1"
                />
              </div>
            </Col>
            <Col span={14} offset={2}>
              <Form.Item name="url" className="no-label">
                <Input suffix name="url" className="token" size="large" />
              </Form.Item>
              <Form.Item name="setting">
                <Checkbox name="setting">
                  {t("translation:setting_201")}
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Row justify="start" gutter={16}>
            <Col span={8}>
              <h3>{t("translation:power_pick_user_password")}</h3>
              <p>{t("translation:ppg_user_access_rights")}</p>
            </Col>
            <Col span={6} offset={2}>
              <Form.Item name="username" className="no-label">
                <Input
                  suffix
                  placeholder={t("translation:power_pick_user")}
                  name="username"
                  className="user"
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="password" className="no-label">
                <Input.Password
                  className="password"
                  onFocus={onFocusHandler}
                  onBlur={onBlurHandler}
                  placeholder={
                    connection.passwordExists
                      ? "***********"
                      : t("translation:power_pick_password")
                  }
                  name="password"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="start" gutter={16}>
            <Col span={4} offset={20}>
              <SubmitButton
                type="primary"
                size="large"
                block
                disabled={!dirty || !isValid}
              >
                {t("translation:save")}
              </SubmitButton>
            </Col>
          </Row>
        </Form>
      ),
      [
        t,
        connection.status,
        connection.passwordExists,
        isTesting,
        testHandler,
        onFocusHandler,
        onBlurHandler,
      ],
    );

  /**
   * Submits the form to update the connection
   * @param values { username: string | undefined; url: string | undefined; password?: string;}
   * @param actions FormikHelpers<FormValues>
   */
  const handleSubmit = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      return await updateConnection({
        id: connection.id,
        ...formatForm(values),
      })
        .then(() => {
          createUpdateSetting({
            number: 201,
            value: values.setting ? "1" : "0",
          });
        })
        .catch((errors) => {
          handleSubmissionErrors(errors, actions.setFieldError);
        });
    },
    [connection.id, createUpdateSetting, updateConnection],
  );

  return (
    <Box>
      <Tabs
        defaultActiveKey="1"
        tabPosition="left"
        items={[
          {
            label: <>{t("translation:settings")}</>,
            key: "1",
            children: !isFetching && (
              <Formik
                component={connectionForm}
                validationSchema={
                  connection.passwordExists
                    ? webServicesSchemaNoPwd(isCloudInstall(installType))
                    : webServicesSchema(isCloudInstall(installType))
                }
                initialValues={{
                  url: connection.url,
                  username: connection.username,
                  password: "",
                  setting: settingValue === "1" ? true : false,
                }}
                enableReinitialize
                onSubmit={handleSubmit}
              />
            ),
          },
          {
            label: t("translation:logs"),
            key: "2",
            children: <Logs isPowerPickConnection connection={connection} />,
          },
        ]}
      />
    </Box>
  );
}
